import { useState, type FormEvent } from 'react';

import { contentTypesUpdate } from '@/shared/api/generated/content/content';
import type { TypeBody } from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { Segmented } from '@/shared/ui/Segmented';
import { Switch } from '@/shared/ui/Switch';
import { toast } from '@/shared/ui/Toast';

import styles from './UsersPage.module.scss';

/**
 * Как посетители входят: открыта ли регистрация, подтверждать ли почту и
 * какие страницы сайта открываются по ссылкам из писем. Это часть схемы:
 * на Production попадает переносом, как поля.
 */
export function UsersSettings({ type, onSaved }: { type: TypeBody; onSaved: () => void }) {
  const auth = type.auth;
  const [registration, setRegistration] = useState(auth?.registration ?? 'open');
  const [confirm, setConfirm] = useState(auth?.confirm_email ?? true);
  const [confirmPath, setConfirmPath] = useState(auth?.confirm_path ?? '/auth/confirm');
  const [resetPath, setResetPath] = useState(auth?.reset_path ?? '/auth/reset');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);

    contentTypesUpdate(
      type.id,
      {
        auth: {
          registration,
          confirm_email: confirm,
          confirm_path: confirmPath.trim(),
          reset_path: resetPath.trim(),
        },
      },
      { 'If-Match': type.etag },
    )
      .then(() => {
        toast.success('Сохранено');
        onSaved();
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.detail : 'Не удалось сохранить');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <form className={styles['form']} onSubmit={submit} noValidate>
      <Field label="Регистрация">
        <Segmented
          value={registration}
          onValueChange={(next) => {
            setRegistration(next as typeof registration);
          }}
          aria-label="Регистрация"
          options={[
            { value: 'open', label: 'Открыта — любой посетитель' },
            { value: 'closed', label: 'Закрыта — только заведённые в админке' },
          ]}
        />
      </Field>

      <Switch checked={confirm} onCheckedChange={setConfirm}>
        Подтверждать почту — войти можно только после ссылки из письма
      </Switch>

      <div className={styles['row']}>
        <Field
          label="Страница подтверждения"
          hint="Ссылка из письма: адрес сайта + этот путь + ?token=…. Страница передаёт код в faceta.auth.confirm."
          error={error}
        >
          <Input
            mono
            value={confirmPath}
            onChange={(event) => {
              setConfirmPath(event.target.value);
            }}
          />
        </Field>
        <Field
          label="Страница нового пароля"
          hint="Передаёт код и пароль в faceta.auth.resetPassword."
        >
          <Input
            mono
            value={resetPath}
            onChange={(event) => {
              setResetPath(event.target.value);
            }}
          />
        </Field>
      </div>

      <p className={styles['muted']}>
        Адрес сайта для ссылок — в «Настройки → Адреса» этого окружения. Письма уходят по SMTP; пока
        почта не настроена на сервере, их видно на вкладке «Письма».
      </p>

      <div>
        <Button type="submit" loading={busy}>
          Сохранить
        </Button>
      </div>
    </form>
  );
}
